import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { NavigateFunction, useNavigate } from 'react-router-dom';

import { FeatureFlags } from '../firebase/featureFlags';
import { ConsumerProfile } from '../types';
import { LanguageCode, type Localization } from '../utils/language';
import { AcceptState } from './acceptScreenSlice';
import { ApiState } from './apiScreenSlice';
import { ApplicationState } from './applicationScreenSlice';
import { ApplicationStatus } from './applicationStatusSlice';
import { selectConsumerInfo } from './consumerInfoSelectors';
import { ConsumerInfo } from './consumerInfoSlice';
import { selectFeatureFlags } from './featureFlagSelectors';
import { selectLanguage, selectLanguageCode } from './languageSelectors';
import { Login } from './loginSlice';
import { selectMerchantData } from './merchantDataSelectors';
import { MerchantData } from './merchantDataSlice';
import { ProductAccount } from './productAccountSlice';
import { ScreenState } from './screenStateSlice';
import {
  selectApiScreenState,
  selectApplicationScreenSelector,
  selectAppStatus,
  selectConsumerProfile,
  selectLoginSelector,
  selectScreenState,
} from './selectors';
import type { AppDispatch, RootState } from './store';

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export interface ImpState {
  language: Localization;
  languageCode: LanguageCode;
  merchantData: MerchantData;
  featureFlags: FeatureFlags;
  consumerInfo: ConsumerInfo;
  screenState: ScreenState;
  appScreenState: ApplicationState;
  loginState: Login;
  appStatus: ApplicationStatus;
  apiScreenState: ApiState;
  consumerProfile: ConsumerProfile;
  acceptScreenState: AcceptState;
  productAccount: ProductAccount;
  dispatch: AppDispatch;
  navigate: NavigateFunction;
}

export const useImpState = (): ImpState => {
  const language = useAppSelector(selectLanguage);
  const languageCode = useAppSelector(selectLanguageCode);
  const merchantData = useAppSelector(selectMerchantData);
  const featureFlags = useAppSelector(selectFeatureFlags);
  const consumerInfo = useAppSelector(selectConsumerInfo);
  const screenState = useAppSelector(selectScreenState);
  const appScreenState = useAppSelector(selectApplicationScreenSelector);
  const loginState = useAppSelector(selectLoginSelector);
  const appStatus = useAppSelector(selectAppStatus);
  const apiScreenState = useAppSelector(selectApiScreenState);
  const consumerProfile = useAppSelector(selectConsumerProfile);
  const acceptScreenState = useAppSelector(
    (state) => state.acceptScreenSelector
  );
  const productAccount = useAppSelector(
    (state) => state.productAccountSelector
  );

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  return {
    language,
    languageCode,
    merchantData,
    featureFlags,
    consumerInfo,
    screenState,
    appScreenState,
    loginState,
    appStatus,
    apiScreenState,
    consumerProfile,
    acceptScreenState,
    productAccount,
    dispatch,
    navigate,
  };
};
